/**
 * API Tracer Service
 * 
 * Comprehensive real-time tracing system for all agent API calls.
 * Captures request/response pairs, timing, errors, and agent context.
 * Provides live monitoring and debugging capabilities.
 */

import { createClient } from '@blinkdotnew/sdk';
import { safeJsonParse } from '@/lib/utils';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

/**
 * API Trace Entry - Complete record of a single API call
 */
export interface APITraceEntry {
  id: string;
  timestamp: number;
  agent: string;
  operation: string;
  provider: 'google' | 'microsoft' | 'blink' | 'internal';
  
  // Request details
  request: {
    method: string;
    url: string;
    headers: Record<string, string>;
    body?: any;
    params?: Record<string, any>;
  };
  
  // Response details
  response?: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body?: any;
    duration: number;
  };
  
  // Execution context
  context: {
    userId?: string;
    sessionId?: string;
    userIntent?: string;
    agentAction?: string;
    confidence?: number;
  };
  
  // Status and errors
  status: 'pending' | 'success' | 'error' | 'timeout';
  error?: {
    message: string;
    code?: string;
    stack?: string;
  };
  
  // Metadata
  metadata: {
    retryCount?: number;
    cacheHit?: boolean;
    tokenRefreshed?: boolean;
  };
}

/**
 * API Trace Session - Groups related traces
 */
export interface APITraceSession {
  id: string;
  userId: string;
  userIntent: string;
  startTime: number;
  endTime?: number;
  traces: APITraceEntry[];
  summary: {
    totalCalls: number;
    successfulCalls: number;
    failedCalls: number;
    totalDuration: number;
    agentChain: string[];
  };
}

/**
 * Live subscriber for trace events
 */
export type TraceListener = (trace: APITraceEntry) => void;
export type SessionListener = (session: APITraceSession) => void;

/**
 * API Tracer Class
 * Main service for intercepting and logging all API calls
 */
export class APITracer {
  private traces: Map<string, APITraceEntry> = new Map();
  private sessions: Map<string, APITraceSession> = new Map();
  private currentSession: string | null = null;
  private listeners: Set<TraceListener> = new Set();
  private sessionListeners: Set<SessionListener> = new Set();
  private isEnabled: boolean = true;

  /**
   * Start a new trace session
   */
  startSession(userId: string, userIntent: string): string {
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const session: APITraceSession = {
      id: sessionId,
      userId,
      userIntent,
      startTime: Date.now(),
      traces: [],
      summary: {
        totalCalls: 0,
        successfulCalls: 0,
        failedCalls: 0,
        totalDuration: 0,
        agentChain: []
      }
    };

    this.sessions.set(sessionId, session);
    this.currentSession = sessionId;

    console.log(`🔍 API Trace Session Started: ${sessionId}`);
    console.log(`   User Intent: "${userIntent}"`);
    
    this.notifySessionListeners(session);
    return sessionId;
  }

  /**
   * End the current trace session
   */
  endSession(sessionId?: string): APITraceSession | null {
    const sid = sessionId || this.currentSession;
    if (!sid) return null;

    const session = this.sessions.get(sid);
    if (!session) return null;

    session.endTime = Date.now();
    
    // Calculate summary
    session.summary.totalCalls = session.traces.length;
    session.summary.successfulCalls = session.traces.filter(t => t.status === 'success').length;
    session.summary.failedCalls = session.traces.filter(t => t.status === 'error').length;
    session.summary.totalDuration = session.traces.reduce((sum, t) => 
      sum + (t.response?.duration || 0), 0
    );
    session.summary.agentChain = [...new Set(session.traces.map(t => t.agent))];

    console.log(`✅ API Trace Session Ended: ${sid}`);
    console.log(`   Total API Calls: ${session.summary.totalCalls}`);
    console.log(`   Success Rate: ${(session.summary.successfulCalls / session.summary.totalCalls * 100).toFixed(1)}%`);
    console.log(`   Total Duration: ${session.summary.totalDuration}ms`);
    console.log(`   Agent Chain: ${session.summary.agentChain.join(' → ')}`);

    this.notifySessionListeners(session);
    
    if (this.currentSession === sid) {
      this.currentSession = null;
    }

    return session;
  }

  /**
   * Trace an API call
   */
  async traceAPICall<T>(
    agent: string,
    operation: string,
    provider: APITraceEntry['provider'],
    request: Omit<APITraceEntry['request'], 'headers'> & { headers?: Record<string, string> },
    context: APITraceEntry['context'],
    executor: () => Promise<T>
  ): Promise<T> {
    if (!this.isEnabled) {
      return await executor();
    }

    const traceId = `trace_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const startTime = Date.now();

    // Sanitize headers (remove sensitive data)
    const sanitizedHeaders = this.sanitizeHeaders(request.headers || {});

    const trace: APITraceEntry = {
      id: traceId,
      timestamp: startTime,
      agent,
      operation,
      provider,
      request: {
        ...request,
        headers: sanitizedHeaders,
        body: this.sanitizeBody(request.body)
      },
      context,
      status: 'pending',
      metadata: {}
    };

    // Store and notify
    this.traces.set(traceId, trace);
    this.addTraceToSession(trace);
    this.notifyListeners(trace);

    console.log(`📡 API Call Traced [${traceId}]`);
    console.log(`   Agent: ${agent}`);
    console.log(`   Operation: ${operation}`);
    console.log(`   Provider: ${provider}`);
    console.log(`   URL: ${request.method} ${request.url}`);

    try {
      const result = await executor();
      
      const duration = Date.now() - startTime;
      
      trace.response = {
        status: 200,
        statusText: 'OK',
        headers: {},
        body: this.sanitizeBody(result),
        duration
      };
      trace.status = 'success';

      console.log(`✅ API Call Succeeded [${traceId}] - ${duration}ms`);
      
      this.notifyListeners(trace);
      this.updateSessionOnSuccess();

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      trace.status = 'error';
      trace.error = {
        message: error instanceof Error ? error.message : 'Unknown error',
        code: (error as any)?.code,
        stack: error instanceof Error ? error.stack : undefined
      };

      if (trace.response) {
        trace.response.duration = duration;
      } else {
        trace.response = {
          status: 500,
          statusText: 'Error',
          headers: {},
          duration
        };
      }

      console.error(`❌ API Call Failed [${traceId}] - ${duration}ms`);
      console.error(`   Error: ${trace.error.message}`);
      
      this.notifyListeners(trace);
      this.updateSessionOnError();

      throw error;
    }
  }

  /**
   * Convenience method to trace a fetch call
   */
  async traceFetch<T = any>(
    agent: string,
    operation: string,
    provider: APITraceEntry['provider'],
    url: string,
    options: RequestInit & { context?: APITraceEntry['context'] } = {}
  ): Promise<{ response: Response; data: T }> {
    const { context, ...fetchOptions } = options;

    return await this.traceAPICall(
      agent,
      operation,
      provider,
      {
        method: fetchOptions.method || 'GET',
        url,
        headers: (fetchOptions.headers as Record<string, string>) || {},
        body: fetchOptions.body ? safeJsonParse(fetchOptions.body as string) : undefined
      },
      context || {},
      async () => {
        const response = await fetch(url, fetchOptions);
        
        // Update trace with actual response details
        const traceId = Array.from(this.traces.keys()).pop();
        if (traceId) {
          const trace = this.traces.get(traceId);
          if (trace && trace.response) {
            trace.response.status = response.status;
            trace.response.statusText = response.statusText;
            
            const headers: Record<string, string> = {};
            response.headers.forEach((value, key) => {
              headers[key] = value;
            });
            trace.response.headers = this.sanitizeHeaders(headers);
          }
        }

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        return { response, data };
      }
    );
  }

  /**
   * Get trace by ID
   */
  getTrace(traceId: string): APITraceEntry | undefined {
    return this.traces.get(traceId);
  }

  /**
   * Get all traces
   */
  getAllTraces(): APITraceEntry[] {
    return Array.from(this.traces.values());
  }

  /**
   * Get traces for a specific user
   */
  getTracesForUser(userId: string): APITraceEntry[] {
    return Array.from(this.traces.values()).filter(trace => {
      // Find the session this trace belongs to
      const session = Array.from(this.sessions.values()).find(s => 
        s.traces.some(t => t.traceId === trace.id)
      );
      return session?.userId === userId;
    });
  }

  /**
   * Get session by ID
   */
  getSession(sessionId: string): APITraceSession | undefined {
    return this.sessions.get(sessionId);
  }

  /**
   * Get current session
   */
  getCurrentSession(): APITraceSession | undefined {
    if (!this.currentSession) return undefined;
    return this.sessions.get(this.currentSession);
  }

  /**
   * Subscribe to trace events
   */
  subscribe(listener: TraceListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Subscribe to session events
   */
  subscribeToSession(listener: SessionListener): () => void {
    this.sessionListeners.add(listener);
    return () => this.sessionListeners.delete(listener);
  }

  /**
   * Enable/disable tracing
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    console.log(`API Tracer ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Clear all traces
   */
  clear(): void {
    this.traces.clear();
    this.sessions.clear();
    this.currentSession = null;
    console.log('API Tracer cleared');
  }

  /**
   * Export session to JSON
   */
  exportSession(sessionId: string): string {
    const session = this.sessions.get(sessionId);
    if (!session) return JSON.stringify({ error: 'Session not found' });
    
    return JSON.stringify(session, null, 2);
  }

  /**
   * Export all traces to JSON
   */
  exportAllTraces(): string {
    return JSON.stringify({
      traces: Array.from(this.traces.values()),
      sessions: Array.from(this.sessions.values())
    }, null, 2);
  }

  // ==================== PRIVATE METHODS ====================

  private addTraceToSession(trace: APITraceEntry): void {
    if (!this.currentSession) return;
    
    const session = this.sessions.get(this.currentSession);
    if (session) {
      session.traces.push(trace);
      
      // Update agent chain
      if (!session.summary.agentChain.includes(trace.agent)) {
        session.summary.agentChain.push(trace.agent);
      }
    }
  }

  private updateSessionOnSuccess(): void {
    if (!this.currentSession) return;
    
    const session = this.sessions.get(this.currentSession);
    if (session) {
      session.summary.totalCalls++;
      session.summary.successfulCalls++;
    }
  }

  private updateSessionOnError(): void {
    if (!this.currentSession) return;
    
    const session = this.sessions.get(this.currentSession);
    if (session) {
      session.summary.totalCalls++;
      session.summary.failedCalls++;
    }
  }

  private notifyListeners(trace: APITraceEntry): void {
    this.listeners.forEach(listener => {
      try {
        listener(trace);
      } catch (error) {
        console.error('Trace listener error:', error);
      }
    });
  }

  private notifySessionListeners(session: APITraceSession): void {
    this.sessionListeners.forEach(listener => {
      try {
        listener(session);
      } catch (error) {
        console.error('Session listener error:', error);
      }
    });
  }

  private sanitizeHeaders(headers: Record<string, string>): Record<string, string> {
    const sanitized: Record<string, string> = {};
    const sensitiveKeys = ['authorization', 'cookie', 'x-api-key', 'api-key'];
    
    Object.entries(headers).forEach(([key, value]) => {
      if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk))) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = value;
      }
    });
    
    return sanitized;
  }

  private sanitizeBody(body: any): any {
    if (!body) return body;
    
    if (typeof body === 'string') {
      // Check if it's a token or key
      if (body.length > 50 && /^[A-Za-z0-9_-]+$/.test(body)) {
        return '[REDACTED_TOKEN]';
      }
      return body;
    }
    
    if (typeof body === 'object') {
      const sanitized: any = Array.isArray(body) ? [] : {};
      const sensitiveKeys = ['password', 'token', 'secret', 'key', 'accessToken', 'refreshToken'];
      
      Object.entries(body).forEach(([key, value]) => {
        if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk))) {
          sanitized[key] = '[REDACTED]';
        } else if (typeof value === 'object' && value !== null) {
          sanitized[key] = this.sanitizeBody(value);
        } else {
          sanitized[key] = value;
        }
      });
      
      return sanitized;
    }
    
    return body;
  }
}

/**
 * Singleton instance
 */
export const apiTracer = new APITracer();

/**
 * Convenience functions for quick tracing
 */

export function startTraceSession(userId: string, userIntent: string): string {
  return apiTracer.startSession(userId, userIntent);
}

export function endTraceSession(sessionId?: string): APITraceSession | null {
  return apiTracer.endSession(sessionId);
}

export async function traceAPICall<T>(
  agent: string,
  operation: string,
  provider: APITraceEntry['provider'],
  request: Omit<APITraceEntry['request'], 'headers'> & { headers?: Record<string, string> },
  context: APITraceEntry['context'],
  executor: () => Promise<T>
): Promise<T> {
  return await apiTracer.traceAPICall(agent, operation, provider, request, context, executor);
}

export async function traceFetch<T = any>(
  agent: string,
  operation: string,
  provider: APITraceEntry['provider'],
  url: string,
  options?: RequestInit & { context?: APITraceEntry['context'] }
): Promise<{ response: Response; data: T }> {
  return await apiTracer.traceFetch(agent, operation, provider, url, options);
}
